import React, { useState, useEffect, useRef } from 'react';
import { eventsAPI } from '../../utils/api';
import useReleaseFilter from '../../hooks/useReleaseFilter';
import ReleaseFilterBar from '../../components/ReleaseFilterBar';

// SourceProtocolMatrixPage — auto-populated equivalent of the hand-
// maintained release_test_matrix. 9 sources × 3 protocols. Each cell
// shows per-device sub-rows (manufacturer / model / soc / android /
// app version / cap score / outcome counts) so QA can see at a glance
// "which device on which build did what" — the same shape as the
// docs/release_test_matrix_v1117.md file but live from telemetry.
//
// The build-version + window + layer filters are now handled by the
// shared <ReleaseFilterBar />, with the same query-param contract used
// on every other admin page (release_op / release_value / days /
// layer). The backend's source_protocol_matrix endpoint applies the
// same filter to both halves of its UNION ALL.

// Display label per canonical content_origin key.
const SOURCE_LABELS = {
    local_file:    'Local Media file',
    cloud_generic: 'Cloud (generic)',
    smb:           'SMB',
    dlna_browse:   'DLNA browse',
    browser_cast:  'Browser cast',
    iptv:          'IPTV (M3U / Xtream)',           // NEW — was missing pre-v1.11.20.5
    media_relay:   'Media relay (YT/HLS)',
    gdrive:        'Google Drive',
    dropbox:       'Dropbox',
    onedrive:      'OneDrive',
};
const PROTOCOL_LABELS = { dlna: 'DLNA', cc: 'Chromecast', miracast: 'Miracast' };

// Normalise legacy cast_sessions keys onto the canonical names used by
// the matrix axes. The legacy table uses 'cloud' / 'cloud_google_drive' /
// 'smb_share' / 'dlna_server' / 'chromecast' / 'local'; the new schema
// uses the canonical names. We collapse both onto canonical here so a
// single matrix row holds both data sources.
//
// 2026-05-12 audit: three additional aliases added after audit found
// the corresponding cells were silently empty even though the app
// fires sessions for them every day. The legacy app emits 'web' from
// BrowserFragment.startBrowserCastSession (FBO + byte-relay both),
// and 'cloud_dropbox' / 'cloud_onedrive' from PlayerActivity's
// cloud-provider classifier. The matrix axes use 'browser_cast' /
// 'dropbox' / 'onedrive' respectively. The 'media_relay' cell is
// still set by an app-side origin override in startPath0ByteRelay
// (v1.12.0.19+) so byte-relay sessions get classified separately
// from pure-FBO browser-cast sessions.
const SOURCE_ALIAS = {
    cloud:              'cloud_generic',
    cloud_google_drive: 'gdrive',
    cloud_dropbox:      'dropbox',
    cloud_onedrive:     'onedrive',
    smb_share:          'smb',
    dlna_server:        'dlna_browse',
    web:                'browser_cast',
};
const PROTOCOL_ALIAS = {
    chromecast: 'cc',
    // 'local' (cast_sessions rows where no cast happened — pure local
    // playback) is intentionally NOT mapped — those rows fall outside
    // the cast-protocol axis and are filtered out below.
};
function canonSource(s)   { return SOURCE_ALIAS[s]   || s || 'unknown'; }
function canonProtocol(p) { return PROTOCOL_ALIAS[p] || p || 'unknown'; }

export default function SourceProtocolMatrixPage() {
    const [data, setData] = useState({ sources: [], protocols: [], rows: [], app_versions: [] });
    const [dmsRows, setDmsRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const intervalRef = useRef(null);
    // The matrix is intentionally cross-release — it shows EVERY build
    // ever tested against EVERY (source × protocol) cell, so the user
    // can see what's still pending vs. what's already validated.
    // Override the global 'current' default to 'all' for this page.
    const filter = useReleaseFilter({ op: 'all' });

    const fetchAll = async () => {
        try {
            // Pass filter.queryParams (release_op / release_value /
            // layer) alongside the existing days+layer args so the
            // matrix scopes to the selected build.
            const res = await eventsAPI.getSourceProtocolMatrix(
                filter.days, filter.layer, filter.queryParams,
            );
            setData(res.data);
        } catch (err) {
            console.error('matrix fetch failed:', err);
        } finally {
            setLoading(false);
        }
        // DMS section runs independently — failure here doesn't break
        // the matrix above.
        try {
            const dms = await eventsAPI.getDmsSessions(filter.days, filter.queryParams);
            setDmsRows(dms.data?.rows || []);
        } catch (err) {
            console.error('dms_sessions fetch failed:', err);
        }
    };

    useEffect(() => {
        fetchAll();
        if (autoRefresh) {
            intervalRef.current = setInterval(fetchAll, 5000);
        }
        return () => intervalRef.current && clearInterval(intervalRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filter.queryParams.release_op,
        filter.queryParams.release_value,
        filter.queryParams.days,
        filter.queryParams.layer,
        autoRefresh,
    ]);

    const currentVersion = data.current_app_version_name || '?';

    // Pivot the flat row list into a {source}{protocol}: row[] map.
    // Legacy aliases collapse onto canonical names (smb_share → smb,
    // chromecast → cc, …) so historical cast_sessions data shows up in
    // the same cells as new v1.11.20 session_terminated events.
    const cells = {};
    for (const r of (data.rows || [])) {
        const s = canonSource(r.content_origin);
        const p = canonProtocol(r.target_protocol);
        // Skip non-cast rows (cast_sessions has 594 rows where protocol
        // is 'local' — pure on-phone playback that doesn't belong on a
        // cast-protocol matrix).
        if (p === 'local' || p === 'unknown') continue;
        cells[s] ??= {};
        cells[s][p] ??= [];
        cells[s][p].push(r);
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-2xl font-bold text-gray-900">Source × Protocol Matrix (live)</h2>
                <div className="flex items-center gap-3 flex-wrap text-sm">
                    <button
                        onClick={() => setAutoRefresh(a => !a)}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                            autoRefresh ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                        {autoRefresh ? 'auto-refresh on' : 'auto-refresh off'}
                    </button>
                    <button onClick={fetchAll} className="px-3 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        refresh now
                    </button>
                </div>
            </div>

            <ReleaseFilterBar
                filter={filter}
                appVersions={data.app_versions || []}
                currentVersion={currentVersion} />

            {loading && data.rows.length === 0 ? (
                <div className="text-gray-400">Loading…</div>
            ) : (
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">Source</th>
                                    {(data.protocols || ['dlna','cc','miracast']).map(p => (
                                        <th key={p} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {PROTOCOL_LABELS[p] || p}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {(data.sources || []).map(s => (
                                    <tr key={s} className="align-top hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                            {SOURCE_LABELS[s] || s}
                                            <div className="text-xs text-gray-400 font-mono mt-1">{s}</div>
                                        </td>
                                        {(data.protocols || ['dlna','cc','miracast']).map(p => {
                                            const rows = cells[s]?.[p] || [];
                                            return <Cell key={p} rows={rows} currentVersion={currentVersion} />;
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="text-xs text-gray-400 px-4 py-2 bg-gray-50 border-t">
                        Window: last {filter.days} day{filter.days === 1 ? '' : 's'} · layer: {filter.layer} · {data.rows.length} cell rows ·
                        cells join events.session_terminated to device_profiles on device_id.
                        Empty cells = no real cast attempts in the window (not the same as "not supported").
                    </div>
                </div>
            )}

            {/* DMS — TV-initiated pull. Separate axis from the matrix above. */}
            <DmsSection rows={dmsRows} days={filter.days} />
        </div>
    );
}

// DmsSection — appended below the main matrix. DMS is the reverse
// direction (TV pulls from phone) so it doesn't share the source ×
// protocol axis. Aggregated by (sink_name, content_origin).
function DmsSection({ rows, days }) {
    return (
        <div className="mt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">DMS Server (TV-initiated pull)</h3>
            <p className="text-xs text-gray-500 mb-3">
                When a TV pulls media from the phone via UPnP/DLNA browse. Aggregated from
                <code className="font-mono mx-1">dms_stream_request</code>,
                <code className="font-mono mx-1">dms_transcode_*</code>,
                <code className="font-mono mx-1">dms_headless_pipeline_*</code>,
                <code className="font-mono mx-1">dms_client_probe_result</code>.
            </p>
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sink (TV)</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attempts</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Success</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fail</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Pull (s)</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Seen</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {(rows || []).map((r, i) => (
                                <tr key={i} className="hover:bg-gray-50">
                                    <td className="px-4 py-2 text-sm font-mono text-gray-900">
                                        {r.sink_name || '?'}
                                        {r.sink_manufacturer && (
                                            <span className="ml-1 text-xs text-gray-500">({r.sink_manufacturer})</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-xs font-mono text-gray-700">{r.content_origin || '?'}</td>
                                    <td className="px-4 py-2 text-sm font-semibold">{r.attempts}</td>
                                    <td className="px-4 py-2 text-sm text-green-700">{r.success}</td>
                                    <td className={`px-4 py-2 text-sm ${r.fail > 0 ? 'text-red-700 font-semibold' : 'text-gray-400'}`}>{r.fail}</td>
                                    <td className="px-4 py-2 text-sm font-mono text-gray-700">
                                        {r.avg_duration_s != null ? Number(r.avg_duration_s).toFixed(1) : '-'}
                                    </td>
                                    <td className="px-4 py-2 text-xs text-gray-500">
                                        {r.last_seen ? new Date(r.last_seen).toLocaleString() : '-'}
                                    </td>
                                </tr>
                            ))}
                            {(!rows || rows.length === 0) && (
                                <tr><td colSpan="7" className="px-4 py-6 text-sm text-gray-400 text-center">
                                    No DMS pull sessions in this window
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="text-xs text-gray-400 px-4 py-2 bg-gray-50 border-t">
                    Window: last {days} day{days === 1 ? '' : 's'} · {rows?.length || 0} sink/source pairs.
                </div>
            </div>
        </div>
    );
}

function Cell({ rows, currentVersion }) {
    if (rows.length === 0) {
        return (
            <td className="px-4 py-3 text-xs text-gray-300">—</td>
        );
    }

    // Aggregate counts across all (device × build) sub-rows
    const totalAttempts = rows.reduce((s, r) => s + r.attempt_count, 0);
    const okAttempts    = rows.filter(r => r.result === 'ok').reduce((s, r) => s + r.attempt_count, 0);
    const errAttempts   = rows.filter(r => r.result === 'error').reduce((s, r) => s + r.attempt_count, 0);
    const overallSymbol = errAttempts === 0 ? 'OK' : (okAttempts === 0 ? 'FAIL' : '~');
    const cellColour    = errAttempts === 0 ? 'bg-green-50' : (okAttempts === 0 ? 'bg-red-50' : 'bg-yellow-50');

    // Group by (device, app_version) to dedupe sub-rows
    const byDevice = {};
    for (const r of rows) {
        const key = `${r.device_id}|${r.app_version_name}`;
        byDevice[key] ??= {
            phone_manufacturer: r.phone_manufacturer,
            phone_model: r.phone_model,
            soc_model: r.soc_model,
            android_version: r.android_version,
            app_version_name: r.app_version_name,
            capability_score: r.capability_score,
            ok: 0, error: 0, other: 0, total: 0, last_seen: r.last_seen,
        };
        const slot = byDevice[key];
        slot.total += r.attempt_count;
        if (r.result === 'ok')         slot.ok    += r.attempt_count;
        else if (r.result === 'error') slot.error += r.attempt_count;
        else                            slot.other += r.attempt_count;
        if (r.last_seen > slot.last_seen) slot.last_seen = r.last_seen;
    }
    const subRows = Object.values(byDevice).sort((a, b) => b.total - a.total);

    return (
        <td className={`px-3 py-3 text-xs align-top ${cellColour}`}>
            <div className="font-bold text-sm mb-1">
                <span className={errAttempts === 0 ? 'text-green-700' : okAttempts === 0 ? 'text-red-700' : 'text-yellow-700'}>{overallSymbol}</span>
                <span className="ml-2 text-gray-600 font-mono">
                    ok={okAttempts}/err={errAttempts}/Σ{totalAttempts}
                </span>
            </div>
            <div className="space-y-1">
                {subRows.map((d, i) => {
                    const isCurrent = currentVersion && d.app_version_name === currentVersion;
                    return (
                        <div key={i} className={`text-xs leading-tight ${isCurrent ? 'border-l-2 border-blue-500 pl-1' : ''}`}>
                            <span className="font-mono text-gray-900">
                                {d.phone_manufacturer} {d.phone_model}
                            </span>
                            <span className="text-gray-500 ml-1">
                                · soc={d.soc_model || '?'} · A{d.android_version}
                            </span>
                            <span className={`ml-1 px-1 rounded font-mono ${
                                isCurrent ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                            }`}>
                                v{d.app_version_name || '?'}{isCurrent ? '' : ' (pre)'}
                            </span>
                            <span className="text-gray-500 ml-1">cap={d.capability_score || '?'}</span>
                            <div className="text-gray-700">
                                ok={d.ok} err={d.error} other={d.other} ·
                                <span className="text-gray-400 ml-1">
                                    last={new Date(d.last_seen).toLocaleString()}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </td>
    );
}
